class CollectionCarouselMobileControl extends HTMLElement {
  constructor() {
    super();
    this.sectionId = "";
    this.mobileCarouselEnabled = false;
    this.totalProducts = 0;
    this.carouselWrapper = null;
    this.carouselContainer = null;
    this.flickityInstance = null;
    this.isMobile = false;
    this.resizeTimeout = null;
    this.handleResize = null;
  }

  static get observedAttributes() {
    return ["section-id", "mobile-carousel-enabled", "total-products"];
  }

  connectedCallback() {
    this.sectionId = this.getAttribute("section-id") || "";
    this.mobileCarouselEnabled =
      this.getAttribute("mobile-carousel-enabled") === "true";
    this.totalProducts = parseInt(this.getAttribute("total-products") || "0");

    this.init();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    switch (name) {
      case "section-id":
        this.sectionId = newValue;
        break;
      case "mobile-carousel-enabled":
        this.mobileCarouselEnabled = newValue === "true";
        this.checkScreenSize();
        break;
      case "total-products":
        this.totalProducts = parseInt(newValue || "0");
        this.checkScreenSize();
        break;
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (!this.sectionId) return;

    this.carouselWrapper = document.querySelector(
      `.collection-carousel-${this.sectionId} .collection-carousel__products-wrapper`
    );
    this.carouselContainer = document.querySelector(
      `.collection-carousel-${this.sectionId}`
    );

    if (!this.carouselWrapper || !this.carouselContainer) return;

    this.checkScreenSize();
    this.setupResizeListener();
  }

  checkScreenSize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;

    this.updateDataAttribute();

    if (wasMobile && !this.isMobile) {
      this.handleDesktopTransition();
    } else if (!wasMobile && this.isMobile) {
      this.handleMobileTransition();
    } else if (!this.flickityInstance) {
      this.initializeCarousel();
    }
  }

  updateDataAttribute() {
    if (!this.carouselContainer) return;

    const layout = this.isMobile
      ? this.mobileCarouselEnabled
        ? "carousel"
        : "stack"
      : "desktop";

    this.carouselContainer.setAttribute("data-mobile-layout", layout);
  }

  handleDesktopTransition() {
    this.destroyFlickity();
    this.initializeCarousel();
  }

  handleMobileTransition() {
    this.destroyFlickity();
    this.initializeCarousel();
  }

  initializeCarousel() {
    if (this.flickityInstance) return;

    const shouldEnable = this.isMobile
      ? this.mobileCarouselEnabled
      : window.innerWidth < 1200 || this.totalProducts > 4;

    if (shouldEnable) {
      this.createFlickityInstance();
    }
  }

  createFlickityInstance() {
    if (
      !this.carouselWrapper ||
      this.carouselWrapper.classList.contains("flickity-enabled")
    ) {
      return;
    }

    if (typeof Flickity === "undefined") return;

    try {
      this.flickityInstance = new Flickity(this.carouselWrapper, {
        cellAlign: "left",
        contain: true,
        responsive: true,
        adaptiveHeight: true,
      });
    } catch (error) {
      this.flickityInstance = null;
    }
  }

  destroyFlickity() {
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }
  }

  setupResizeListener() {
    this.handleResize = this.debounce(() => {
      this.checkScreenSize();
    }, 250);

    window.addEventListener("resize", this.handleResize);
  }

  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  cleanup() {
    this.destroyFlickity();

    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
      this.handleResize = null;
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
  }

  destroy() {
    this.cleanup();
  }

  refresh() {
    this.checkScreenSize();
  }

  get isCarouselEnabled() {
    return !!this.flickityInstance;
  }

  get currentLayout() {
    return (
      this.carouselContainer?.getAttribute("data-mobile-layout") || "unknown"
    );
  }
}

customElements.define(
  "collection-carousel-mobile-control",
  CollectionCarouselMobileControl
);

window.initCollectionCarouselMobileControl = function (
  sectionId,
  mobileCarouselEnabled,
  totalProducts
) {
  const element = document.createElement("collection-carousel-mobile-control");
  element.setAttribute("section-id", sectionId);
  element.setAttribute(
    "mobile-carousel-enabled",
    mobileCarouselEnabled.toString()
  );
  element.setAttribute("total-products", totalProducts.toString());
  element.style.display = "none";
  document.body.appendChild(element);
  return element;
};
